import axios from 'axios';

/**
 * Resolves the client's public IP address for stamping onto consent acceptances.
 *
 * The backend cannot reliably derive the caller IP on every browser / network
 * path (proxy and CDN header gaps), so we resolve it client-side and send it
 * explicitly. Providers are tried in order; if all fail we return a sentinel
 * address so onboarding is never blocked by an IP lookup.
 *
 * All requests use bare `axios` (NOT the app's `RequestApi`) so the request
 * interceptor does not attach `Authorization` / `X-Api-Key` headers or the API
 * base URL to these third-party calls.
 */

/** Valid IPv4 sentinel used when no provider can resolve the public IP. */
export const FALLBACK_IP_ADDRESS = process.env.REACT_APP_IP_FALLBACK || '0.0.0.0';

const LOOKUP_TIMEOUT_MS = 3000;

interface IpProvider {
  url: string;
  /** Extracts the IP string from the provider's response body. */
  parse: (data: unknown) => string;
}

/**
 * Ordered list of IP providers (first successful wins). URLs are overridable via
 * env so infra can swap or allow-list them without a code change.
 *  1. ipify   — JSON:  { "ip": "1.2.3.4" }
 *  2. icanhazip (Cloudflare) — plain text: "1.2.3.4\n"
 */
const IP_PROVIDERS: IpProvider[] = [
  {
    url: 'https://api.ipify.org?format=json',
    parse: (data: any) => (typeof data === 'string' ? data : data?.ip) || '',
  },
  {
    url: 'https://icanhazip.com',
    parse: (data: any) => (typeof data === 'string' ? data.trim() : ''),
  },
];

const IPV4_PATTERN = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

/**
 * Guards against a provider returning an error page / HTML instead of an IP.
 * Accepts well-formed IPv4 or (loosely) IPv6.
 */
const isValidIpAddress = (value: string): boolean => {
  if (!value) return false;
  const ipv4Match = value.match(IPV4_PATTERN);
  if (ipv4Match) {
    return ipv4Match.slice(1).every(octet => Number(octet) <= 255);
  }
  // Loose IPv6 check — enough to reject non-IP payloads while allowing IPv6 clients.
  return value.includes(':') && /^[0-9a-fA-F:]+$/.test(value);
};

// Session-level memoisation. Only a real IP is cached; a fallback result is not,
// so a later call can retry the providers.
let cachedIp: string | null = null;
let inFlightLookup: Promise<string> | null = null;

const resolveFromProviders = async (): Promise<string> => {
  for (const provider of IP_PROVIDERS) {
    try {
      const response = await axios.get(provider.url, { timeout: LOOKUP_TIMEOUT_MS });
      const ip = provider.parse(response.data);
      if (isValidIpAddress(ip)) {
        return ip;
      }
    } catch {
      // Provider unreachable/blocked/timed out — fall through to the next one.
    }
  }
  return FALLBACK_IP_ADDRESS;
};

/**
 * Returns the client's public IP address (best-effort). Never rejects — resolves
 * to `FALLBACK_IP_ADDRESS` when every provider fails. Memoised for the session so
 * repeated calls (e.g. warming on the Signature step, then submitting) reuse the
 * first successful lookup.
 */
export const getClientIpAddress = async (): Promise<string> => {
  if (cachedIp) {
    return cachedIp;
  }
  if (!inFlightLookup) {
    inFlightLookup = resolveFromProviders()
      .then(ip => {
        if (ip !== FALLBACK_IP_ADDRESS) {
          cachedIp = ip;
        }
        return ip;
      })
      .finally(() => {
        inFlightLookup = null;
      });
  }
  return inFlightLookup;
};
